// Staff auth. dev: X-Dev-User header. cognito: in-app SRP sign-in (USER_SRP_AUTH) with
// amazon-cognito-identity-js; tokens live in tab-scoped storage and the ID token goes out as a
// Bearer token, refreshed before expiry.
import type {
  CognitoUser, CognitoUserPool, CognitoUserSession, IAuthenticationCallback, ICognitoStorage,
} from 'amazon-cognito-identity-js';
import { setAdminAuth } from './api';
import { makeCognitoTabStorage, type CognitoTabStorage } from './cognitoTabStorage';
import { config, loadConfig } from './config';

export const ADMIN_GROUP = 'platform-admin';
/** The staff app's name: its browser tab, and the label in people's authenticator apps. */
export const APP_NAME = 'Vertekal Platform Manager';
const REFRESH_MARGIN_MS = 120_000;

/** Pool password policy (infrastructure/modules/cognito/main.tf password_policy). */
export const PASSWORD_MIN_LENGTH = 12;
// Cognito's accepted special characters.
const SYMBOLS = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/;
export const PASSWORD_RULES: Array<{ id: string; label: string; test: (pw: string) => boolean }> = [
  { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (pw) => pw.length >= PASSWORD_MIN_LENGTH },
  { id: 'upper', label: 'An uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'A lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'A number', test: (pw) => /[0-9]/.test(pw) },
  { id: 'symbol', label: 'A symbol, such as ! @ # $ or %', test: (pw) => SYMBOLS.test(pw.trim()) },
];
export const passwordMeetsPolicy = (pw: string) => pw === pw.trim() && PASSWORD_RULES.every((r) => r.test(pw));

export type SessionInfo = { idToken: string; email: string; groups: string[]; expiresAt: number };
export const isPlatformAdmin = (s: SessionInfo) => s.groups.includes(ADMIN_GROUP);

/** Where the sign-in flow goes next. */
export type SignInStep =
  | { kind: 'done' }
  | { kind: 'newPassword'; notice?: string }
  | { kind: 'mfaSetup'; secret: string; otpauthUrl: string; notice?: string }
  | { kind: 'totp'; notice?: string };

/** A sign-in error with copy fit to show the user. */
export class AuthError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

type Phase = 'signIn' | 'newPassword' | 'totp' | 'mfaSetup' | 'forgot' | 'reset';
type CognitoErr = { code?: string; name?: string; message?: string };

const MSG = {
  credentials: 'Email or password is incorrect.',
  tempExpired: 'Your temporary password has expired. Ask an admin to resend your invite.',
  tooMany: 'Too many attempts. Wait a few minutes and try again.',
  network: 'We couldn’t reach the sign-in service. Check your connection and try again.',
  totpMismatch: 'That code didn’t match. Enter the current code from your authenticator app.',
  emailCodeMismatch: 'That code didn’t match. Check the email and try again.',
  emailCodeExpired: 'That code has expired. Request a new one.',
  weakPassword: 'That password doesn’t meet the rules below.',
  sessionExpired: 'Your sign-in timed out. Start again.',
  resetRequired: 'You need to reset your password. Choose “Forgot password?” to get a code.',
  generic: 'Something went wrong. Try again.',
};

/** Maps a Cognito error to user-facing copy. Never reveals whether an account exists. */
export function friendlyError(e: unknown, phase: Phase): AuthError {
  if (e instanceof AuthError) return e;
  const err = (e ?? {}) as CognitoErr;
  const code = err.code ?? err.name ?? '';
  const msg = err.message ?? '';
  if (code === 'NetworkError' || msg === 'Network error' || e instanceof TypeError) return new AuthError('network', MSG.network);
  if (code === 'TooManyRequestsException' || code === 'LimitExceededException' || code === 'TooManyFailedAttemptsException') {
    return new AuthError('too_many', MSG.tooMany);
  }
  if (code === 'NotAuthorizedException') {
    if (/temporary password has expired/i.test(msg)) return new AuthError('temp_expired', MSG.tempExpired);
    if (/attempts exceeded/i.test(msg)) return new AuthError('too_many', MSG.tooMany);
    if (/session/i.test(msg) && phase !== 'signIn') return new AuthError('session_expired', MSG.sessionExpired);
    return new AuthError('credentials', MSG.credentials);
  }
  if (code === 'UserNotFoundException' || code === 'UserNotConfirmedException') return new AuthError('credentials', MSG.credentials);
  if (code === 'PasswordResetRequiredException') return new AuthError('reset_required', MSG.resetRequired);
  if (code === 'CodeMismatchException' || code === 'EnableSoftwareTokenMFAException') {
    return new AuthError('code_mismatch', phase === 'reset' ? MSG.emailCodeMismatch : MSG.totpMismatch);
  }
  if (code === 'ExpiredCodeException') {
    return new AuthError('code_expired', phase === 'reset' ? MSG.emailCodeExpired : MSG.totpMismatch);
  }
  if (code === 'InvalidPasswordException') return new AuthError('weak_password', MSG.weakPassword);
  if (code === 'InvalidParameterException' && (phase === 'newPassword' || phase === 'reset') && /password/i.test(msg)) {
    return new AuthError('weak_password', MSG.weakPassword);
  }
  return new AuthError('unknown', MSG.generic);
}

// ---------------------------------------------------------------- pool

type CognitoLib = typeof import('amazon-cognito-identity-js');
let libPromise: Promise<CognitoLib> | null = null;
const loadLib = () => (libPromise ??= import('amazon-cognito-identity-js'));

let storage: CognitoTabStorage | null = null;
const tabStorage = () => (storage ??= makeCognitoTabStorage());

let pool: CognitoUserPool | null = null;
async function getPool(): Promise<CognitoUserPool> {
  if (pool) return pool;
  const [lib, c] = await Promise.all([loadLib(), loadConfig()]);
  if (!c.cognitoUserPoolId || !c.cognitoClientId) throw new AuthError('unconfigured', 'Sign-in isn’t set up for this environment.');
  pool = new lib.CognitoUserPool({
    UserPoolId: c.cognitoUserPoolId,
    ClientId: c.cognitoClientId,
    Storage: tabStorage() as ICognitoStorage,
    ...(c.cognitoRegion ? { endpoint: `https://cognito-idp.${c.cognitoRegion}.amazonaws.com/` } : {}),
  });
  return pool;
}

function toInfo(user: CognitoUser, session: CognitoUserSession): SessionInfo {
  const id = session.getIdToken();
  const p = id.decodePayload() as { email?: string; exp?: number; 'cognito:groups'?: string[] };
  return {
    idToken: id.getJwtToken(),
    email: p.email ?? user.getUsername(),
    groups: Array.isArray(p['cognito:groups']) ? p['cognito:groups'] : [],
    expiresAt: (p.exp ?? 0) * 1000,
  };
}

let active: { user: CognitoUser; info: SessionInfo } | null = null;
let refreshing: Promise<SessionInfo | null> | null = null;

function getSessionOf(user: CognitoUser): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    user.getSession((err: Error | null, s: CognitoUserSession | null) => resolve(err || !s || !s.isValid() ? null : s));
  });
}

function forceRefresh(user: CognitoUser, session: CognitoUserSession): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    user.refreshSession(session.getRefreshToken(), (err: unknown, s: CognitoUserSession | null) => resolve(err || !s ? null : s));
  });
}

/**
 * The current session, refreshed when the ID token is within two minutes of expiry.
 * Null when signed out or when the refresh token is expired or revoked.
 */
export function currentSession(opts: { force?: boolean } = {}): Promise<SessionInfo | null> {
  if (!opts.force && active && active.info.expiresAt - Date.now() > REFRESH_MARGIN_MS) return Promise.resolve(active.info);
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const p = await getPool();
      const user = active?.user ?? p.getCurrentUser();
      if (!user) { active = null; return null; }
      let session = await getSessionOf(user);
      if (session && (opts.force || session.getIdToken().getExpiration() * 1000 - Date.now() <= REFRESH_MARGIN_MS)) {
        session = await forceRefresh(user, session);
      }
      if (!session) { clearLocal(user); return null; }
      active = { user, info: toInfo(user, session) };
      return active.info;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function clearLocal(user?: CognitoUser | null) {
  try { (user ?? active?.user)?.signOut(); } catch { /* ignore */ }
  tabStorage().clear();
  active = null;
}

/** Revokes this session's refresh token (best effort, bounded) and clears this tab's tokens. */
export async function signOut(): Promise<void> {
  pending = null;
  if (config().authMode !== 'cognito') return;
  let user: CognitoUser | null = active?.user ?? null;
  try { user ??= (await getPool()).getCurrentUser(); } catch { user = null; }
  if (user) {
    const u = user;
    await Promise.race([
      new Promise<void>((resolve) => { try { u.signOut(() => resolve()); } catch { resolve(); } }),
      new Promise<void>((resolve) => setTimeout(resolve, 3000)),
    ]);
  }
  clearLocal(user);
}

// ---------------------------------------------------------------- sign-in flow

/** The sign-in in progress. The password is kept only so an expired challenge session can restart quietly. */
let pending: { user: CognitoUser; email: string; password: string; required: string[] } | null = null;

export function otpauthUrl(email: string, secret: string): string {
  const issuer = encodeURIComponent(APP_NAME);
  return `otpauth://totp/${issuer}:${encodeURIComponent(email)}?secret=${secret}&issuer=${issuer}`;
}

/** Callbacks shared by every call that can finish sign-in or raise the next challenge. */
function flow(user: CognitoUser, phase: Phase, resolve: (s: SignInStep) => void, reject: (e: AuthError) => void): IAuthenticationCallback {
  return {
    onSuccess: (session) => {
      active = { user, info: toInfo(user, session) };
      pending = null;
      resolve({ kind: 'done' });
    },
    onFailure: (err) => reject(friendlyError(err, phase)),
    newPasswordRequired: (_attrs: Record<string, string>, required: string[] | undefined) => {
      if (pending) pending.required = (required ?? []).filter((a) => a !== 'email' && a !== 'email_verified');
      resolve({ kind: 'newPassword' });
    },
    mfaSetup: () => {
      user.associateSoftwareToken({
        associateSecretCode: (secret) => resolve({ kind: 'mfaSetup', secret, otpauthUrl: otpauthUrl(pending?.email ?? user.getUsername(), secret) }),
        onFailure: (err) => reject(friendlyError(err, 'mfaSetup')),
      });
    },
    totpRequired: () => resolve({ kind: 'totp' }),
    selectMFAType: () => user.sendMFASelectionAnswer('SOFTWARE_TOKEN_MFA', flow(user, phase, resolve, reject) as never),
    mfaRequired: () => reject(new AuthError('unsupported', 'This account uses a sign-in method this app doesn’t support. Ask an admin for help.')),
    customChallenge: () => reject(new AuthError('unsupported', 'This account uses a sign-in method this app doesn’t support. Ask an admin for help.')),
  };
}

const run = (fn: (resolve: (s: SignInStep) => void, reject: (e: AuthError) => void) => void) =>
  new Promise<SignInStep>((resolve, reject) => {
    try { fn(resolve, reject); } catch (e) { reject(friendlyError(e, 'signIn')); }
  });

/** Step 1: email + password over SRP. */
export async function startSignIn(email: string, password: string): Promise<SignInStep> {
  const lib = await loadLib();
  const p = await getPool();
  clearLocal();
  const user = new lib.CognitoUser({ Username: email.trim(), Pool: p, Storage: tabStorage() as ICognitoStorage });
  user.setAuthenticationFlowType('USER_SRP_AUTH');
  pending = { user, email: email.trim(), password, required: [] };
  const details = new lib.AuthenticationDetails({ Username: email.trim(), Password: password });
  return run((resolve, reject) => user.authenticateUser(details, flow(user, 'signIn', resolve, reject)));
}

/** Challenge sessions last a few minutes. When one lapses, sign in again with the password we hold. */
async function restartAfterTimeout(phase: Phase, attempt: () => Promise<SignInStep>, notice: (s: SignInStep) => SignInStep): Promise<SignInStep> {
  try {
    return await attempt();
  } catch (e) {
    const err = friendlyError(e, phase);
    if (err.code !== 'session_expired' || !pending) throw err;
    const { email, password } = pending;
    return notice(await startSignIn(email, password));
  }
}

/** NEW_PASSWORD_REQUIRED: first sign-in with the emailed temporary password. */
export function submitNewPassword(newPassword: string): Promise<SignInStep> {
  const cur = pending;
  if (!cur) return Promise.reject(new AuthError('session_expired', MSG.sessionExpired));
  return restartAfterTimeout('newPassword', async () => {
    const step = await run((resolve, reject) => {
      // Cognito rejects email/email_verified here; send only attributes it says are required and missing.
      const attrs: Record<string, string> = {};
      for (const a of cur.required) attrs[a] = '';
      cur.user.completeNewPasswordChallenge(newPassword, cur.required.length ? attrs : {}, flow(cur.user, 'newPassword', resolve, reject));
    });
    // The new password is set once Cognito accepts it, even when another challenge follows.
    if (pending) pending.password = newPassword;
    return step;
  }, (s) => (s.kind === 'newPassword' ? { kind: 'newPassword', notice: 'That took a little long. Enter your new password again.' } : s));
}

/** MFA_SETUP: verify the first code from the authenticator app. Completes sign-in. */
export function submitMfaSetupCode(code: string): Promise<SignInStep> {
  const cur = pending;
  if (!cur) return Promise.reject(new AuthError('session_expired', MSG.sessionExpired));
  return restartAfterTimeout('mfaSetup', async () => {
    const step = await run((resolve, reject) => {
      cur.user.verifySoftwareToken(code, 'Authenticator app', {
        onSuccess: (session: CognitoUserSession) => {
          active = { user: cur.user, info: toInfo(cur.user, session) };
          pending = null;
          resolve({ kind: 'done' });
        },
        onFailure: (err: unknown) => reject(friendlyError(err, 'mfaSetup')),
      });
    });
    // Make TOTP the preferred factor. The pool already requires it, so a failure here is harmless.
    await new Promise<void>((resolve) => {
      try { cur.user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, () => resolve()); } catch { resolve(); }
    });
    return step;
  }, (s) => s.kind === 'totp'
    ? { kind: 'totp', notice: 'Your authenticator app is set up. Enter the next code it shows to finish signing in.' }
    : s.kind === 'mfaSetup' ? { ...s, notice: 'That took a little long, so here is a new code. Remove the old entry from your app and scan this one.' } : s);
}

/** SOFTWARE_TOKEN_MFA: the 6-digit code at every sign-in. */
export function submitTotp(code: string): Promise<SignInStep> {
  const cur = pending;
  if (!cur) return Promise.reject(new AuthError('session_expired', MSG.sessionExpired));
  return restartAfterTimeout('totp', () => run((resolve, reject) => {
    cur.user.sendMFACode(code, flow(cur.user, 'totp', resolve, reject), 'SOFTWARE_TOKEN_MFA');
  }), (s) => (s.kind === 'totp' ? { kind: 'totp', notice: 'That took a little long. Enter the current code from your app.' } : s));
}

/** Forgot password, step 1: Cognito emails a code. Succeeds the same way whether or not the account exists. */
export async function requestPasswordReset(email: string): Promise<void> {
  const lib = await loadLib();
  const p = await getPool();
  const user = new lib.CognitoUser({ Username: email.trim(), Pool: p, Storage: tabStorage() as ICognitoStorage });
  await new Promise<void>((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => {
        const e = friendlyError(err, 'forgot');
        // Don't reveal whether the account exists.
        if (e.code === 'credentials') resolve(); else reject(e);
      },
    });
  });
}

/** Forgot password, step 2: emailed code + new password. */
export async function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  const lib = await loadLib();
  const p = await getPool();
  const user = new lib.CognitoUser({ Username: email.trim(), Pool: p, Storage: tabStorage() as ICognitoStorage });
  await new Promise<void>((resolve, reject) => {
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => {
        const e = friendlyError(err, 'reset');
        reject(e.code === 'credentials' ? new AuthError('code_mismatch', MSG.emailCodeMismatch) : e);
      },
    });
  });
}

// ---------------------------------------------------------------- API wiring

export const authMode = () => config().authMode;

let toSignIn: () => void = () => {
  const next = location.pathname + location.search;
  location.assign(`/admin/sign-in?next=${encodeURIComponent(next)}`);
};
/** Lets the router replace the full-page redirect used when a session ends mid-use. */
export const setSignInRedirect = (fn: () => void) => { toSignIn = fn; };

setAdminAuth({
  async headers(): Promise<Record<string, string>> {
    const c = await loadConfig();
    if (c.authMode === 'dev') return { 'X-Dev-User': c.devUser };
    const s = await currentSession();
    return s ? { Authorization: `Bearer ${s.idToken}` } : {};
  },
  async onUnauthorized() {
    if (config().authMode === 'dev') return false;
    if (await currentSession({ force: true })) return true;
    clearLocal();
    toSignIn();
    return false;
  },
});

/** Test hook: forget module state between tests. */
export function __resetAuthForTests() {
  pool = null; storage = null; active = null; pending = null; refreshing = null;
}
